import { useMemo, useState } from 'react'
import { formatScheduleLabel, useTimeFormat } from '../../lib/timeFormat'
import { periodDates, periodNumberFor, periodRangeLabel } from '../../lib/payPeriods'
import ExportBar from './ExportBar'
import PayPeriodCalendar from './PayPeriodCalendar'
import { ReportSelect } from './ReportControls'
import ReportTable, { type ReportTableColumn } from './ReportTable'
import { passes, type ColumnSelection } from './columnFilters'
import { getTeamLookups } from './coverageReportStore'
import { todayIso } from './reportDates'
import { nextSort, sortRows, type ReportSort, type SortValue } from './reportSort'
import { buildPayPeriodGrid, cellText, filterPayPeriodGrid, leaveTypesIn, type PayPeriodFilters, type PayPeriodRow } from './payPeriodGridFigures'
import { summarizeByDate, toReportRows, type CoverageReportStatus } from './coverageReport'
import { dayHeading, payPeriodGridTable } from './payPeriodGridExport'
import { getPayPeriodGrid } from './payPeriodGridStore'
import StaffingSummaryTable from './StaffingSummaryTable'
import { filterStaffing, staffingByDayAssignment, staffingExportSummary, type StaffingFilters } from './staffingSummary'
import { useCoverageData } from './useCoverageData'
import { useLoaded } from './useLoaded'

/**
 * docs/product/reports.md's pay period schedule: every officer's published
 * schedule for one pay period, a day at a time, with the shift, days off, and
 * leave by type.
 */
const EMPTY = { people: [], entries: [], publishedTeamIds: new Set<string>() }
const NO_TEAMS = { teams: new Map<string, string>(), supervisors: new Map<string, string>() }

export default function PayPeriodSchedule() {
  const { format } = useTimeFormat()
  const currentPeriod = useMemo(() => periodNumberFor(todayIso()), [])
  const periodChoices = [currentPeriod + 1, currentPeriod, currentPeriod - 1, currentPeriod - 2, currentPeriod - 3, currentPeriod - 4, currentPeriod - 5]
  const [period, setPeriod] = useState(currentPeriod)
  const [filters, setFilters] = useState<PayPeriodFilters>({})
  const [staffingFilters, setStaffingFilters] = useState<StaffingFilters>({})
  const [view, setView] = useState<'list' | 'calendar'>('list')
  const [sort, setSort] = useState<ReportSort>({ column: 'unit', direction: 'asc' })
  const { data, loading, failed } = useLoaded(
    async () => {
      const [grid, lookups] = await Promise.all([getPayPeriodGrid(period), getTeamLookups()])
      return { grid, lookups }
    },
    { grid: EMPTY, lookups: NO_TEAMS },
    [period],
  )
  const { grid, lookups } = data

  const days = useMemo(() => periodDates(period), [period])
  const coverage = useCoverageData(days[0].date, days[days.length - 1].date)
  // The staffing figures follow the unit and shift filters; a title or a name
  // does not change how many people a shift requires.
  const filteredCoverage = useMemo(
    () => toReportRows(coverage.rows, coverage.lookups).filter((row) => passes(filters.unit, row.teamId) && passes(filters.shift, row.shift)),
    [coverage.rows, coverage.lookups, filters.unit, filters.shift],
  )
  const staffing = useMemo(() => {
    const names = new Map(days.map((day) => [day.date, dayHeading(day)]))
    return staffingByDayAssignment(filteredCoverage, (date) => names.get(date) ?? date)
  }, [filteredCoverage, days])
  // How each day's staffing meets the targets, for the calendar.
  const statusByDate = useMemo(
    () => new Map<string, CoverageReportStatus>(summarizeByDate(filteredCoverage).map((day) => [day.date, day.status])),
    [filteredCoverage],
  )

  const staffingShown = useMemo(() => filterStaffing(staffing, staffingFilters), [staffing, staffingFilters])

  const built = useMemo(
    () => buildPayPeriodGrid(grid.people, grid.entries, days.map((day) => day.date), lookups.teams, lookups.supervisors),
    [grid, days, lookups],
  )
  const filtered = useMemo(() => filterPayPeriodGrid(built, filters), [built, filters])
  const leaveTypes = useMemo(() => leaveTypesIn(filtered), [filtered])

  // Each column offers every value in the schedule, not only those left by the
  // other columns' filters, so a filter can always be widened again.
  const valuesOf = (pick: (row: PayPeriodRow) => string) => Array.from(new Set(built.map(pick))).sort().map((name) => ({ value: name, label: name }))
  const filterFor = (key: keyof PayPeriodFilters, options: { value: string; label: string }[]) => ({
    options,
    selected: filters[key],
    onChange: (selected: ColumnSelection) => setFilters((current) => ({ ...current, [key]: selected })),
  })
  const units = Array.from(new Set(built.map((row) => row.teamId)))
    .map((id) => ({ value: id, label: lookups.teams.get(id) ?? 'Unknown unit' }))
    .sort((a, b) => a.label.localeCompare(b.label))
  const people = Array.from(new Map(built.map((row) => [row.employeeId, row.identifier ? `${row.employee} (${row.identifier})` : row.employee])))
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const columns: ReportTableColumn<PayPeriodRow>[] = [
    { id: 'unit', label: 'Unit', filter: filterFor('unit', units), cell: (row) => row.unit },
    { id: 'shift', label: 'Shift', filter: filterFor('shift', valuesOf((row) => row.shift)), cell: (row) => row.shift },
    { id: 'title', label: 'Title', filter: filterFor('title', valuesOf((row) => row.title)), cell: (row) => row.title },
    { id: 'employee', label: 'Name', filter: filterFor('employee', people), cell: (row) => row.employee },
    ...days.map((day, index): ReportTableColumn<PayPeriodRow> => ({
      id: `day-${index}`,
      label: dayHeading(day),
      cell: (row) => formatScheduleLabel(cellText(row.cells[index]), format),
    })),
    { id: 'workDays', label: 'Work days', numeric: true, cell: (row) => row.workDays },
    { id: 'daysOff', label: 'Days off', numeric: true, cell: (row) => row.daysOff },
    ...leaveTypes.map((type): ReportTableColumn<PayPeriodRow> => ({ id: `leave-${type}`, label: type, numeric: true, cell: (row) => row.leave[type] ?? 0 })),
    { id: 'otherDays', label: 'Other', numeric: true, cell: (row) => row.otherDays },
  ]
  const sortValue = (column: string): ((row: PayPeriodRow) => SortValue) => {
    if (column.startsWith('day-')) return (row) => cellText(row.cells[Number(column.slice(4))]) || null
    if (column.startsWith('leave-')) return (row) => row.leave[column.slice(6)] ?? 0
    const fixed: Record<string, (row: PayPeriodRow) => SortValue> = {
      unit: (row) => row.unit,
      shift: (row) => row.shift,
      title: (row) => row.title,
      employee: (row) => row.employee,
      workDays: (row) => row.workDays,
      daysOff: (row) => row.daysOff,
      otherDays: (row) => row.otherDays,
    }
    return fixed[column] ?? fixed.unit
  }
  const rows = useMemo(() => sortRows(filtered, sortValue(sort.column), sort.direction), [filtered, sort])

  const unpublished = Array.from(lookups.teams.entries())
    .filter(([id]) => !grid.publishedTeamIds.has(id))
    .map(([, name]) => name)
    .sort()
  const periodLabel = `Pay Period ${period}, ${periodRangeLabel(period)}`

  return (
    <div className="flex flex-col gap-5">
      <section aria-label="Pay period" className="rounded-card border border-line bg-canvas p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-1 text-caption font-bold text-ink-muted">
            Pay period
            <ReportSelect value={period} onChange={(event) => {
                setPeriod(Number(event.target.value))
                setStaffingFilters({})
                setFilters((current) => ({ ...current, employee: undefined }))
              }}>
              {periodChoices.map((number) => (
                <option key={number} value={number}>
                  Pay Period {number}, {periodRangeLabel(number)}
                </option>
              ))}
            </ReportSelect>
          </label>
        </div>
      </section>

      {!loading && !failed && unpublished.length > 0 && (
        <p role="status" className="rounded-card border border-line bg-canvas p-3 text-small text-ink-muted">
          No published schedule for this pay period yet: {unpublished.join(', ')}.
        </p>
      )}

      <ExportBar
        loading={loading}
        disabled={loading || failed || coverage.loading || coverage.failed || rows.length === 0}
        fileStem={`muster-pay-period-${period}-schedule`}
        table={() => ({ ...payPeriodGridTable(rows, { periodLabel, days, filters, teams: lookups.teams, generatedAt: new Date() }), summary: staffingExportSummary('Staffing against targets', staffingShown) })}
      />

      <div role="group" aria-label="Schedule view" className="flex">
        {(['list', 'calendar'] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={view === option}
            onClick={() => setView(option)}
            className={`min-h-11 border border-line-strong px-4 text-small font-bold first:rounded-l-control last:rounded-r-control ${
              view === option ? 'border-primary bg-selected' : 'hover:bg-surface-2'
            }`}
          >
            {option === 'list' ? 'List' : 'Calendar'}
          </button>
        ))}
      </div>

      {failed ? (
        <p role="alert" className="rounded-card border border-line bg-canvas p-4 text-small text-error">
          The pay period schedule could not be loaded. Reload the page, or try again in a moment.
        </p>
      ) : view === 'calendar' ? (
        loading ? (
          <p role="status" className="text-small text-ink-muted">Loading the schedule&hellip;</p>
        ) : rows.length === 0 ? (
          <p className="rounded-card border border-line bg-canvas p-4 text-small text-ink-muted">
            No officers to show. The schedule for this pay period may not be published, or a filter may exclude everyone.
          </p>
        ) : (
          <PayPeriodCalendar rows={rows} dates={days.map((day) => day.date)} label={`Calendar of the published schedule for ${periodLabel}`} statusByDate={statusByDate} />
        )
      ) : (
        <ReportTable
          label="Pay period schedule"
          caption={`Each officer's published schedule for ${periodLabel}, with days off and leave by type`}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.key}
          sort={sort}
          onSort={(column) => setSort((current) => nextSort(current, column))}
          onClearFilters={() => setFilters({})}
          loading={loading}
          empty="No officers to show. The schedule for this pay period may not be published, or a filter may exclude everyone."
          minWidth={2200}
        />
      )}

      <StaffingSummaryTable
        heading="Staffing against targets"
        note="By day and assignment for the pay period, from the staffing targets. The unit and shift filters apply."
        summary={staffing}
        shown={staffingShown}
        filters={staffingFilters}
        onFiltersChange={setStaffingFilters}
        loading={coverage.loading}
        failed={coverage.failed}
        empty="No staffing targets or scheduled officers for this pay period."
      />
    </div>
  )
}
